import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getStats, getCalls } from '../services/api';
import socket from '../services/socket';
import StatCard from '../components/StatCard';
import { SafetyBadge, CallStatusBadge } from '../components/StatusBadge';

function Dashboard() {
  const [stats, setStats] = useState({ total: 0, answered: 0, needs_help: 0, no_response: 0, unknown: 0, help_by_category: [] });
  const [recentCalls, setRecentCalls] = useState([]);

  const loadData = async () => {
    try {
      const [statsRes, callsRes] = await Promise.all([getStats(), getCalls({ limit: 10 })]);
      setStats(statsRes.data);
      setRecentCalls(callsRes.data.calls);
    } catch (err) {
      console.error('Failed to load dashboard data:', err);
    }
  };

  useEffect(() => {
    loadData();
    socket.on('stats_update', loadData);
    socket.on('call_update', loadData);
    socket.on('call_completed', loadData);
    return () => {
      socket.off('stats_update', loadData);
      socket.off('call_update', loadData);
      socket.off('call_completed', loadData);
    };
  }, []);

  const visitBranchCalls = recentCalls.filter((call) => call.selected_action === 'visit_branch').slice(0, 8);
  const sendLinkCalls = recentCalls.filter((call) => call.selected_action === 'send_link').slice(0, 8);
  const callbackCalls = recentCalls.filter((call) => call.selected_action === 'callback_agent').slice(0, 8);
  const actionCounts = (stats.help_by_category || []).reduce((acc, item) => {
    acc[item._id] = item.count;
    return acc;
  }, {});

  const renderCallList = (calls, emptyMessage, showStatus = false) => {
    if (calls.length === 0) {
      return <p className="text-muted mb-0">{emptyMessage}</p>;
    }

    return (
      <div className="list-group list-group-flush">
        {calls.map((call) => (
          <Link
            key={call._id}
            to={`/calls/${call._id}`}
            className="list-group-item list-group-item-action px-0 border-0"
          >
            <div className="d-flex justify-content-between align-items-center gap-2">
              <div>
                <strong className="d-block small">{call.employee_name}</strong>
                <small className="text-muted">{call.phone}</small>
              </div>
              {showStatus ? <CallStatusBadge status={call.call_status} /> : <SafetyBadge status={call.safety_status} />}
            </div>
          </Link>
        ))}
      </div>
    );
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h4 className="fw-bold mb-1">Dashboard</h4>
          <p className="text-muted mb-0">Real-time pending KYC call overview</p>
        </div>
        <Link to="/dial" className="btn btn-primary">
          <i className="bi bi-telephone-plus me-1"></i> New Call
        </Link>
      </div>

      <div className="row g-2 mb-4">
        <div className="col-lg col-md-4 col-sm-6">
          <StatCard icon="bi-telephone" label="Total Calls" value={stats.total} color="#0d6efd" bgColor="#e7f1ff" />
        </div>
        <div className="col-lg col-md-4 col-sm-6">
          <StatCard icon="bi-telephone-inbound" label="Calls Answered" value={stats.answered} color="#6610f2" bgColor="#ede7f6" />
        </div>
        <div className="col-lg col-md-4 col-sm-6">
          <StatCard icon="bi-building" label="Visit Branch" value={actionCounts.visit_branch || 0} color="#198754" bgColor="#d1e7dd" />
        </div>
        <div className="col-lg col-md-4 col-sm-6">
          <StatCard icon="bi-chat-text" label="KYC Link Sending" value={actionCounts.send_link || 0} color="#0dcaf0" bgColor="#cff4fc" />
        </div>
        <div className="col-lg col-md-4 col-sm-12">
          <StatCard icon="bi-telephone" label="Callback Requested" value={actionCounts.callback_agent || 0} color="#dc3545" bgColor="#f8d7da" />
        </div>
      </div>

      <div className="row g-4">
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body">
              <h6 className="fw-bold mb-3"><i className="bi bi-building me-2"></i>Visit Branch</h6>
              {renderCallList(visitBranchCalls, 'No branch visit selections yet')}
            </div>
          </div>
        </div>
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body">
              <h6 className="fw-bold mb-3"><i className="bi bi-chat-text me-2"></i>KYC Link Sending</h6>
              {renderCallList(sendLinkCalls, 'No WhatsApp link requests yet')}
            </div>
          </div>
        </div>
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body">
              <h6 className="fw-bold mb-3"><i className="bi bi-telephone me-2"></i>Callback Requested</h6>
              {renderCallList(callbackCalls, 'No callback requests yet')}
            </div>
          </div>
        </div>
        <div className="col-lg-3">
          <div className="card border-0 shadow-sm h-100" style={{ borderRadius: 16 }}>
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="fw-bold mb-0"><i className="bi bi-clock-history me-2"></i>Call Logs</h6>
                <Link to="/logs" className="btn btn-sm btn-outline-primary">View All</Link>
              </div>
              {renderCallList(recentCalls.slice(0, 8), 'No calls yet', true)}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
